/* SPDX-License-Identifier: AGPL-3.0-or-later */

import { Component, inject, input } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SharedMediaEndpointService } from '@api';

@Component({
  selector: 'app-image-upload',
  standalone: true,
  imports: [
    MatCardModule,
    MatIcon,
    MatButtonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
  ],
  templateUrl: './image-upload.component.html',
  styleUrl: './image-upload.component.scss',
})
export class ImageUploadComponent {
  parsedImage: string | null = null;
  errorMessage: string = '';
  providedImage: Blob | null = null;

  author: string = '';
  title: string = '';

  tournamentId = input<number>();
  token = input.required<string>();

  sharedMediaService = inject(SharedMediaEndpointService);
  snackBar = inject(MatSnackBar);

  uploadImage() {
    console.log(this.author === '' ? 'PartyGuest' : this.author);
    if (this.providedImage && this.tournamentId) {
      const createDto = {
        author: this.author === '' ? 'PartyGuest' : this.author,
        title:
          this.title === ''
            ? `t-${this.tournamentId()}_${Date.now().toString()}_submission.jpg`
            : this.title,
        tournamentId: this.tournamentId()!,
      };
      this.sharedMediaService.createSharedMedia(createDto, this.providedImage).subscribe({
        next: () => {
          console.log('Succesfully created');
          this.snackBar.open('Succesfully uploaded image.', 'OK', { duration: 2500 });
          this.clear();
        },
        error: () => {
          console.error('Error when uplaoding image.');
          this.errorMessage = 'Error occured when uploading image.';
        },
      });
    }
  }

  clear() {
    this.parsedImage = null;
    this.providedImage = null;
    this.errorMessage = '';
    this.title = '';
    this.author = '';
  }

  async onFileSelected(event: Event): Promise<void> {
    this.errorMessage = '';
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file && file.type === 'image/jpeg') {
      const reader = new FileReader();
      reader.onload = async (e: ProgressEvent<FileReader>) => {
        const img = new Image();
        img.onload = async () => {
          try {
            this.providedImage = await this.formatImageTo4by3(img);
            this.parsedImage = await this.blobToDataURL(this.providedImage);
          } catch (error) {
            console.log(error);
            this.errorMessage = 'Error while processing the image, try again please';
          }
        };
        if (e.target && e.target.result) {
          img.src = e.target.result as string;
        }
      };
      reader.readAsDataURL(file);
    } else {
      this.errorMessage = 'Please provide a JPEG image';
    }
  }

  formatImageTo4by3(image: HTMLImageElement): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (ctx) {
        const aspectRatio = 4 / 3;
        const imageAspectRatio = image.width / image.height;
        let newWidth, newHeight, offsetX, offsetY;

        if (imageAspectRatio > 1) {
          // Landscape orientation
          if (imageAspectRatio > aspectRatio) {
            // Image is wider than the target aspect ratio
            newHeight = image.height;
            newWidth = image.height * aspectRatio;
            offsetX = (image.width - newWidth) / 2;
            offsetY = 0;
          } else {
            // Image is narrower than the target aspect ratio
            newWidth = image.width;
            newHeight = image.width / aspectRatio;
            offsetX = 0;
            offsetY = (image.height - newHeight) / 2;
          }
        } else {
          // Portrait orientation
          if (imageAspectRatio > 1 / aspectRatio) {
            // Image is wider than the target aspect ratio for portrait
            newHeight = image.height;
            newWidth = image.height * (1 / aspectRatio);
            offsetX = (image.width - newWidth) / 2;
            offsetY = 0;
          } else {
            // Image is narrower than the target aspect ratio for portrait
            newWidth = image.width;
            newHeight = image.width * aspectRatio;
            offsetX = 0;
            offsetY = (image.height - newHeight) / 2;
          }
        }

        // Manually checking if image is bigger than 10 MB not needed because of lossy JPG
        // Only server should check
        canvas.width = newWidth;
        canvas.height = newHeight;

        ctx.drawImage(image, offsetX, offsetY, newWidth, newHeight, 0, 0, newWidth, newHeight);

        canvas.toBlob(blob => {
          if (blob) {
            console.log(blob.size);
            resolve(blob);
          } else {
            reject(new Error('Error extracting cropped image'));
          }
        }, 'image/jpeg');
      } else {
        reject(new Error('Context is not available'));
      }
    });
  }

  blobToDataURL(blob: Blob): Promise<string | null> {
    if (!this.providedImage) {
      this.providedImage = null;
      this.errorMessage = 'Blob is null, please reload.';
      return Promise.reject(null);
    }

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (reader.result) {
          resolve(reader.result as string);
        } else {
          this.errorMessage = 'Could not convert blob to data URL';
          reject(null);
        }
      };
      reader.readAsDataURL(blob);
    });
  }
}
